import { GripVertical, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { ConfirmModal } from "@/components/ConfirmModal";
import { LayoutEditor } from "@/components/LayoutEditor";
import { UpdateWeeksModal } from "@/components/UpdateWeeksModal";
import { WidgetPicker } from "@/components/WidgetPicker";
import { WidgetPreviewCard } from "@/components/WidgetPreviewCard";
import { WidgetSettingsModal } from "@/components/WidgetSettingsModal";
import { useSettings } from "@/hooks/useSettings";
import { useOpenWeeks } from "@/hooks/useWeeks";
import {
  DAY_KEYS,
  DAY_LABELS_SHORT,
  type DayKey,
  dayKeyFromIndex,
} from "@/lib/days";
import { groupByRow, maxRow } from "@/lib/layout";
import {
  addWidget,
  type CascadeImpact,
  deleteWidget,
  deleteWidgetWithCascade,
  findCascadeImpact,
  replaceWidgets,
  updateWidget,
} from "@/lib/repos/settings";
import { applyMalToWeeks } from "@/lib/repos/weeks";
import type { WidgetConfig } from "@/lib/schema";
import { findWeekUpdateCandidates } from "@/lib/week-updates";
import { getWidgetModule, WIDGET_REGISTRY } from "@/widgets/registry";

type Scope = "day" | "week";

type ModalState =
  | { kind: "none" }
  | { kind: "picker" }
  | { kind: "settings"; mode: "create" | "edit"; initial: WidgetConfig }
  | { kind: "delete"; widget: WidgetConfig; impact: CascadeImpact }
  | { kind: "update-weeks" };

function todayDayKey(): DayKey {
  const idx = (new Date().getDay() + 6) % 7;
  return dayKeyFromIndex(idx);
}

function widgetScope(w: WidgetConfig): Scope {
  return (getWidgetModule(w.kind)?.scope ?? "day") as Scope;
}

export function WeekTemplate() {
  const { settings, loading } = useSettings();
  const [scope, setScope] = useState<Scope>("day");
  const [selectedDay, setSelectedDay] = useState<DayKey>(() => todayDayKey());
  const [modal, setModal] = useState<ModalState>({ kind: "none" });
  const [editingLayout, setEditingLayout] = useState(false);

  const scopeWidgets = useMemo(
    () => settings.widgets.filter((w) => widgetScope(w) === scope),
    [settings.widgets, scope],
  );
  const rows = useMemo(() => groupByRow(scopeWidgets), [scopeWidgets]);

  // Completed weeks are frozen by definition, so the open ones are the only
  // place an update could land (docs/adr/0014).
  const { weeks: openWeeks } = useOpenWeeks();
  const updateCandidates = useMemo(
    () => findWeekUpdateCandidates(openWeeks, settings.widgets, new Date()),
    [openWeeks, settings.widgets],
  );

  const handlePick = (kind: string) => {
    const module = WIDGET_REGISTRY[kind];
    if (!module) return;
    const draft = module.defaults(settings.widgets) as WidgetConfig;
    setModal({ kind: "settings", mode: "create", initial: draft });
  };

  const handleSettingsSave = async (next: WidgetConfig) => {
    if (modal.kind !== "settings") return;
    if (modal.mode === "create") {
      const placed: WidgetConfig = {
        ...next,
        row: maxRow(scopeWidgets) + 1,
        column: 0,
      };
      await addWidget(placed);
    } else {
      await updateWidget(next.id, next);
    }
    setModal({ kind: "none" });
  };

  const handleEdit = (widget: WidgetConfig) => {
    setModal({ kind: "settings", mode: "edit", initial: widget });
  };

  const handleRequestDelete = (widget: WidgetConfig) => {
    const impact = findCascadeImpact(settings.widgets, widget.id);
    setModal({ kind: "delete", widget, impact });
  };

  const handleDeleteConfirm = async () => {
    if (modal.kind !== "delete") return;
    if (modal.impact.affected.length > 0) {
      await deleteWidgetWithCascade(modal.widget.id);
    } else {
      await deleteWidget(modal.widget.id);
    }
    setModal({ kind: "none" });
  };

  const deleteWidgetForUi = modal.kind === "delete" ? modal.widget : null;
  const deleteImpact = modal.kind === "delete" ? modal.impact : null;
  const cascadeBody =
    deleteImpact && deleteImpact.affected.length > 0
      ? describeCascade(deleteImpact)
      : "Dette kan ikke angres. Uker som allerede inneholder widgeten beholder den til du oppdaterer dem.";

  return (
    <div
      data-screen-label="Ukesmal"
      className={updateCandidates.length > 0 ? "has-update-bar" : undefined}
    >
      <p className="page-eyebrow">Konfigurasjon</p>
      <h1 className="page-title">Ukesmal</h1>
      <p className="page-sub">Sett opp widgets som logges hver dag og uke.</p>
      <p className="page-note" data-testid="ukesmal-future-note">
        Uker du oppretter senere bruker alltid denne malen.
      </p>

      <div className="settings-toolbar">
        {!editingLayout ? (
          <button
            type="button"
            className="btn-primary icon-btn"
            onClick={() => setModal({ kind: "picker" })}
            data-testid="add-widget-button"
          >
            <Plus size={16} />
            Ny widget
          </button>
        ) : null}
        {scopeWidgets.length > 0 && !editingLayout ? (
          <button
            type="button"
            className="btn ghost icon-btn"
            onClick={() => setEditingLayout(true)}
            data-testid="layout-edit-toggle"
          >
            <GripVertical size={16} />
            Endre rekkefølge
          </button>
        ) : null}
      </div>

      {editingLayout ? (
        <LayoutEditor
          widgets={scopeWidgets}
          onSave={async (nextScopeWidgets) => {
            const others = settings.widgets.filter(
              (w) => widgetScope(w) !== scope,
            );
            await replaceWidgets([...others, ...nextScopeWidgets]);
            setEditingLayout(false);
          }}
          onCancel={() => setEditingLayout(false)}
        />
      ) : (
        <>
          <div className="settings-tabs" role="tablist" aria-label="Omfang">
            <button
              type="button"
              role="tab"
              aria-selected={scope === "day"}
              className={`settings-tab ${scope === "day" ? "active" : ""}`}
              onClick={() => setScope("day")}
              data-testid="settings-tab-daily"
            >
              Daglig
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={scope === "week"}
              className={`settings-tab ${scope === "week" ? "active" : ""}`}
              onClick={() => setScope("week")}
              data-testid="settings-tab-weekly"
            >
              Ukentlig
            </button>
          </div>

          {scope === "day" ? (
            <div className="day-pills" role="tablist" aria-label="Velg ukedag">
              {DAY_KEYS.map((k) => (
                <button
                  type="button"
                  key={k}
                  role="tab"
                  aria-selected={selectedDay === k}
                  className={`day-pill ${selectedDay === k ? "active" : ""}`}
                  onClick={() => setSelectedDay(k)}
                  data-testid={`day-pill-${k}`}
                >
                  {DAY_LABELS_SHORT[k]}
                </button>
              ))}
            </div>
          ) : null}

          {loading ? (
            <p>Laster…</p>
          ) : rows.length === 0 ? (
            <p data-testid="settings-empty">
              Ingen widgets enda. Klikk «Ny widget» for å komme i gang.
            </p>
          ) : (
            <div className="preview-grid" data-testid="preview-grid">
              {rows.map((row) => (
                <div
                  key={row.row}
                  className="preview-row"
                  data-testid={`preview-row-${row.row}`}
                >
                  {row.widgets.map((widget) => (
                    <WidgetPreviewCard
                      key={widget.id}
                      widget={widget}
                      selectedDay={selectedDay}
                      allWidgets={settings.widgets}
                      onEdit={() => handleEdit(widget)}
                      onDelete={() => handleRequestDelete(widget)}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <WidgetPicker
        open={modal.kind === "picker"}
        scope={scope}
        onClose={() => setModal({ kind: "none" })}
        onPick={handlePick}
      />

      <WidgetSettingsModal
        open={modal.kind === "settings"}
        mode={modal.kind === "settings" ? modal.mode : "create"}
        initial={modal.kind === "settings" ? modal.initial : null}
        allWidgets={settings.widgets}
        onSave={handleSettingsSave}
        onCancel={() => setModal({ kind: "none" })}
      />

      <ConfirmModal
        open={modal.kind === "delete"}
        title={`Slette «${deleteWidgetForUi?.label ?? ""}»?`}
        body={cascadeBody}
        confirmLabel="Slett"
        confirmIcon={<Trash2 size={16} />}
        cancelLabel="Avbryt"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setModal({ kind: "none" })}
        data-testid="delete-widget-modal"
      />

      <UpdateWeeksModal
        open={modal.kind === "update-weeks"}
        candidates={updateCandidates}
        onClose={() => setModal({ kind: "none" })}
        onApply={async (weekIds) => {
          await applyMalToWeeks(weekIds, settings.widgets);
        }}
      />

      {updateCandidates.length > 0 ? (
        <div className="update-weeks-bar" data-testid="update-weeks-bar">
          <span className="update-weeks-bar-text">
            {summariseDrift(updateCandidates)}
          </span>
          <button
            type="button"
            className="btn-primary"
            onClick={() => setModal({ kind: "update-weeks" })}
            data-testid="update-weeks-button"
          >
            Oppdater åpne uker
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Names the weeks rather than counting them: one or two rows is the normal
 * case, and "Uke 32 bruker en eldre mal" says what a bare count cannot.
 */
function summariseDrift(
  candidates: { data: { weekNumber: number } }[],
): string {
  const numbers = candidates.map((c) => `Uke ${c.data.weekNumber}`);
  if (numbers.length === 1) return `${numbers[0]} bruker en eldre mal`;
  const last = numbers.pop();
  return `${numbers.join(", ")} og ${last} bruker en eldre mal`;
}

function describeCascade(impact: CascadeImpact): string {
  const parts: string[] = [];
  for (const a of impact.affected) {
    if (a.effect.kind === "meal-log-summary") {
      parts.push(`• ${a.widgetLabel} → mister sin kilde`);
      continue;
    }
    for (const r of a.effect.rows) {
      const rowName = r.rowName.trim() || "(uten navn)";
      const note = r.willBeDropped ? " (raden fjernes — siste widget)" : "";
      parts.push(`• ${a.widgetLabel} → ${rowName}${note}`);
    }
  }
  return `Widgeten brukes av følgende. Lagrede uker beholder referansen.\n${parts.join("\n")}`;
}
